import { ConnectNext, Spinner, theme } from '@ahmdigital/ui';
import { get, snakeCase, split, toLower } from 'lodash/fp';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import { NextSeo } from 'next-seo';
import type { History } from 'history';
import type { Store } from 'redux';
// @ts-expect-error - Automatic, Please fix when editing this file
import { PersistGate as PersistGateClient } from 'redux-persist/lib/integration/react';
import { ThemeProvider } from 'styled-components';
import { useRouter } from 'next/router';
import { useStore } from 'react-redux';
import React, { ComponentType, ReactNode, useEffect, useLayoutEffect, useMemo } from 'react';
// @ts-expect-error - Automatic, Please fix when editing this file
import liveChatActions from '@ahmdigital/livechat/lib/actions';
import loggerFactory from '@ahmdigital/logger-frontend';
import NextImage from 'next/image';
import NextLink from 'next/link';
import Script from 'next/script';

import '../src/client/application/base/index.sass';
import '@ahmdigital/ui/lib/style.css';
import { nextReduxWrapper } from '../src/client/store/create-store';
import analytics from '../src/client/analytics';
import Application from '../src/client/application';
import buildBreadcrumbListSchema from '../src/client/components/structured-data/utils/build-breadcrumb-list-schema';
import config from '../src/client/config';
import createHistory from '../src/client/store/create-history';
import CustomApolloProvider from '../src/client/client-app/custom-apollo-provider';
import CustomRouter from '../src/client/components/custom-router';
import FaviconHeader from './favicon-header';
import GA4Analytics from '../src/client/analytics/ga4-analytics';
import liveChatConfig from '../src/client/utils/livechat';
import LocationActions from '../src/client/actions/location';
import logging from '../src/client/logging';
import makeGetCustomData from '../src/client/utils/make-get-custom-data';
import ScrollToView from '../src/utils/scroll-to-view';
import StructuredData from '../src/client/components/structured-data';
import useHydratePreviewOptions from '../src/client/cms/utils/use-hydrate-preview-options';
import useInitGrowthBook from '../src/client/growthbook';
import useIsAfterHydration from '../src/client/hooks/use-is-after-hydration';
import versionInfo from '../.version.json';
import type { RootState } from '../src/client/reducers';

const buildLogging = (reduxStore: Store<RootState>) => {
  const getCustomData = makeGetCustomData({ reduxStore });
  // We are using versionInfo as the default export
  // because using named exports from JSON modules is not supported in Webpack 5.
  const { version } = versionInfo;

  const logger = loggerFactory(config.get('loggingClientSide'), {
    React,
    application: config.get('app'),
    getCustomData,
    releaseStage: config.get('NODE_ENV'),
    version,
  });
  logging.setLogger(logger);
  return logging;
};

// Fixes a persist gate issue with static optimisation
// https://github.com/rt2zz/redux-persist/issues/576
const PersistGateServer = ({ children }: { children: ReactNode }) => children;

type AppProps = {
  Component: ComponentType<Record<string, unknown> & { history: History }>;
  pageProps?: {} | null;
};

const useBrowserLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

const ScrollHandler = () => {
  setTimeout(ScrollToView, 1000);
  return null;
};

const App = ({ Component, pageProps = null }: AppProps) => {
  const router = useRouter();
  const { asPath } = router;
  const history = createHistory(asPath);
  const isAfterHydration = useIsAfterHydration();
  const store = useStore<RootState>();
  // @ts-expect-error - Automatic, Please fix when editing this file
  store.dispatch(LocationActions.load());
  analytics.getState = store.getState;

  useBrowserLayoutEffect(() => {
    (window as Window & { AHM_HYDRATION_COMMITTED?: boolean }).AHM_HYDRATION_COMMITTED = true;
  }, []);

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      analytics.publish('pageview', { location: url });
    };

    // Record the pageview event for the initial page load
    analytics.publish('pageview', { location: asPath });

    router.events.on('routeChangeComplete', handleRouteChange);

    // If the component is unmounted, unsubscribe from the event with the `off` method
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const gb = useInitGrowthBook(asPath);

  useHydratePreviewOptions();

  const PersistGate = isAfterHydration ? PersistGateClient : PersistGateServer;

  // this function will be called once the livechat action has been reduced into state
  // so we need to assign new values from the redux state into GA‘s dimensions
  // and then fire an action to analytics.
  // @ts-expect-error - Automatic, Please fix when editing this file
  const publishChatState = (data) => {
    analytics.publish('profile:assign');
    analytics.publish('action', {
      action: snakeCase(`on_page_chat_${data.state}`),
      category: 'chat',
      label: data.conversationId,
    });
  };

  // @ts-expect-error - Automatic, Please fix when editing this file
  const submitChatStateToGA4 = (liveChatState) => {
    GA4Analytics.submit({
      data: liveChatState,
      name: 'on_page_chat',
    });
  };

  const CANONICAL_DOMAIN = 'https://ahm.com.au';
  const canonicalFragments = split('?', CANONICAL_DOMAIN + (router.asPath === '/' ? '' : router.asPath));
  const canonicalUrl = toLower(canonicalFragments[0]!);

  const ErrorBoundary = useMemo(() => buildLogging(store).getLogger().getErrorBoundary(), [store]);

  const breadcrumbList = buildBreadcrumbListSchema(asPath);

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <PersistGate loading={<Spinner />} persistor={get('__persistor', store)}>
          <GrowthBookProvider growthbook={gb}>
            <CustomRouter history={history}>
              <ConnectNext hasNextJs nextLink={NextLink} nextImage={NextImage} nextRouter={router} isModernNextJs>
                <CustomApolloProvider>
                  <FaviconHeader />
                  {breadcrumbList && <StructuredData data={breadcrumbList} />}
                  <Application>
                    {/* next/script <Script> cannot be placed inside next/head <Head> */}
                    <Script id="lazyLoaded" strategy="lazyOnload">
                      {isAfterHydration
                        ? liveChatActions.load(
                            liveChatConfig,
                            undefined,
                            publishChatState,
                            submitChatStateToGA4,
                          )(store.dispatch)
                        : null}
                    </Script>

                    <Script id="anchor-scroll" strategy="afterInteractive">
                      <ScrollHandler />
                    </Script>

                    <NextSeo defaultTitle="Cheap private health insurance" canonical={canonicalUrl} />
                    <Component {...pageProps} history={history} key={router.asPath} />
                  </Application>
                </CustomApolloProvider>
              </ConnectNext>
            </CustomRouter>
          </GrowthBookProvider>
        </PersistGate>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default nextReduxWrapper.withRedux(App);
